import type { ClientOrganization } from '@DigitaleDoerfer/digitale-doerfer-api';
import { GetOrganizationsInGeoAreaTagsEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import { BottomSheetFlatList, BottomSheetModal } from '@gorhom/bottom-sheet';
import type { FC, Ref } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { ActivityIndicator, Button, Text } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { WEB_SHEET_MAX_WIDTH } from '@/constants/web';
import { useGetOrganizationsInGeoArea } from '@/features/api/hooks/grapevine-organization/useGetOrganizationsInGeoArea';
import { GeoAreaRoute } from '@/features/geo-area/navigation/geoAreaRoute';
import { OrganizationListItem } from '@/features/profile-setting/components/OrganizationListItem';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppNavigation } from '@/navigation/hooks/useAppNavigation';
import { EmptyCard } from '@/shared/components/EmptyCard';
import { SheetBackdrop } from '@/shared/components/SheetBackdrop';
import { isWeb } from '@/shared/utils/platform';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const PARTNER_TAGS = [GetOrganizationsInGeoAreaTagsEnum.Partner];

const makeStyles = (theme: AppTheme, webSideInset: number) =>
  StyleSheet.create({
    sheetBackground: { backgroundColor: theme.colors.surfaceContainerLow },
    sheetHandleIndicator: { backgroundColor: theme.colors.outline },
    header: { paddingHorizontal: 16, paddingTop: 8, paddingBottom: 16, gap: 4 },
    title: { color: theme.colors.onSurface },
    subtitle: { color: theme.colors.onSurfaceVariant },
    listContent: { paddingHorizontal: 12 },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      padding: 32
    },
    empty: { marginHorizontal: 16 },
    webSheet: {
      left: webSideInset,
      right: webSideInset,
      backgroundColor: theme.colors.surfaceContainerLow,
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.15)',
      elevation: 8
    }
  });

type Props = {
  ref: Ref<BottomSheetModal | null>;
  /** Geo area whose subtree of PARTNER orgs to list (typically ancestors.at(-2)). */
  geoAreaId?: string;
  /** Display label for the region — shown in the sheet header. */
  regionName?: string;
};

export const RegionPartnersSheet: FC<Props> = ({
  ref,
  geoAreaId,
  regionName
}) => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  const webSideInset = isWeb
    ? Math.max((windowWidth - WEB_SHEET_MAX_WIDTH) / 2, 0)
    : 0;
  const styles = useMemo(
    () => makeStyles(theme, webSideInset),
    [theme, webSideInset]
  );
  const navigation = useAppNavigation();

  const { t } = useTranslation(LocalizationNameSpace.GEO_AREA, {
    keyPrefix: 'components.regionPartnersSheet'
  });

  // Tracks the sheet's presented state so the partners query only runs while
  // the sheet is actually open. Driven by onChange so it stays in sync with
  // the modal regardless of how it was opened/closed.
  const [isOpen, setIsOpen] = useState(false);

  const handleSheetChange = useCallback((index: number) => {
    setIsOpen(index >= 0);
  }, []);

  const geoAreaIds = useMemo(() => (geoAreaId ? [geoAreaId] : []), [geoAreaId]);

  const {
    data: partners,
    isPending,
    isError,
    refetch
  } = useGetOrganizationsInGeoArea({
    geoAreaIds,
    tags: PARTNER_TAGS,
    enabled: isOpen
  });

  const handleRowPress = useCallback(
    (id: string) => {
      if (ref && typeof ref === 'object') {
        ref.current?.dismiss();
      }
      navigation.navigate(GeoAreaRoute.OrganizationProfile, { id });
    },
    [navigation, ref]
  );

  const renderItem = ({ item }: { item: ClientOrganization }) => (
    <OrganizationListItem
      organization={item}
      onPress={() => handleRowPress(item.id)}
    />
  );

  const listHeader = (
    <View style={styles.header}>
      <Text
        variant="titleLarge"
        style={styles.title}
        testID="meine-region-partners-title"
      >
        {t('title')}
      </Text>
      {regionName && (
        <Text variant="bodyMedium" style={styles.subtitle}>
          {regionName}
        </Text>
      )}
    </View>
  );

  const renderBody = () => {
    if (isPending) {
      return (
        <View style={styles.center}>
          {listHeader}
          <ActivityIndicator size={48} />
        </View>
      );
    }
    if (isError) {
      return (
        <View style={styles.center}>
          {listHeader}
          <Text variant="titleMedium">{t('state.error.title')}</Text>
          <Text variant="bodyMedium">{t('state.error.subtitle')}</Text>
          <Button mode="contained-tonal" onPress={() => refetch()}>
            {t('state.error.retry')}
          </Button>
        </View>
      );
    }
    return (
      <BottomSheetFlatList
        data={partners ?? []}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        ListHeaderComponent={listHeader}
        ListEmptyComponent={
          <EmptyCard style={styles.empty} description={t('empty')} />
        }
        contentContainerStyle={{
          ...styles.listContent,
          paddingBottom: insets.bottom
        }}
        showsVerticalScrollIndicator={false}
      />
    );
  };

  return (
    <BottomSheetModal
      ref={ref}
      enableDynamicSizing={false}
      snapPoints={['90%']}
      topInset={insets.top}
      onChange={handleSheetChange}
      // Android + New Architecture: the content pan gesture (used to drag the
      // sheet from its scrollable content) conflicts with the FlatList's
      // native scroll gesture and blocks scrolling entirely. Disabling it
      // keeps the handle drag/pan-down-to-close working while letting the
      // BottomSheetFlatList scroll normally.
      enableContentPanningGesture={false}
      backgroundStyle={
        isWeb ? { backgroundColor: 'transparent' } : styles.sheetBackground
      }
      style={isWeb ? styles.webSheet : undefined}
      handleIndicatorStyle={styles.sheetHandleIndicator}
      backdropComponent={SheetBackdrop}
    >
      {renderBody()}
    </BottomSheetModal>
  );
};
